// Core Entity Types - aligned with backend User model
export interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: UserRole;
  is_active: boolean;
  district_id?: number;
  school_id?: number;
  employee_id?: string;
  department?: string;
  phone?: string;
  created_at?: string;
  updated_at?: string;
  // Populated fields (from relationships)
  district_name?: string;
  school_name?: string;
  // Auth-specific fields
  is_superuser?: boolean;
  last_login?: string;
  avatar?: string;
  // Permissions payload from backend
  permissions?: { permission: string; scope: string; description: string }[];
  accessible_districts?: number[];
  accessible_schools?: number[];
}

export interface District {
  id: number;
  name: string;
  code: string;
  state: string;
  country: string;
  city?: string;
  zip_code?: string;
  superintendent?: string;
  email?: string;
  phone?: string;
  address?: string;
  website?: string;
  created_at: string;
  updated_at: string;
}

export type DistrictCreateFormData = Omit<
  District,
  'id' | 'created_at' | 'updated_at'
>;
export type DistrictUpdateFormData = Partial<
  Omit<District, 'id' | 'created_at' | 'updated_at'>
>;

export interface School {
  id: number;
  name: string;
  code: string;
  school_type: string;
  grade_levels: string;
  district_id: number;
  principal?: string;
  email?: string;
  phone?: string;
  address?: string;
  student_count?: number;
  created_at: string;
  updated_at: string;
  district?: {
    id: number;
    name: string;
    code: string;
    state: string;
    country: string;
  };
  user_count?: number;
}

export type SchoolCreateFormData = Omit<
  School,
  'id' | 'created_at' | 'updated_at' | 'district' | 'user_count'
>;
export type SchoolUpdateFormData = Partial<
  Omit<School, 'id' | 'created_at' | 'updated_at' | 'district' | 'user_count'>
>;

// Announcement Types
export interface Announcement {
  id: string;
  title: string;
  content: string;
  summary?: string;
  type: 'general' | 'emergency' | 'event' | 'academic' | 'policy' | 'reminder';
  priority: 'low' | 'medium' | 'high' | 'critical';
  status:
    | 'draft'
    | 'pending_approval'
    | 'approved'
    | 'scheduled'
    | 'sent'
    | 'failed'
    | 'cancelled';
  created_by: string;
  approved_by?: string;
  approved_at?: string;
  target_roles?: string[];
  target_grades?: string[];
  channels: string[];
  scheduled_for?: string;
  expires_at?: string;
  total_recipients: number;
  delivery_count: number;
  read_count: number;
  engagement_count: number;
  contains_pii: boolean;
  ferpa_approved: boolean;
  school_id?: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

export type AnnouncementCreateFormData = Omit<
  Announcement,
  | 'id'
  | 'created_at'
  | 'updated_at'
  | 'total_recipients'
  | 'delivery_count'
  | 'read_count'
  | 'engagement_count'
  | 'contains_pii'
  | 'ferpa_approved'
  | 'approved_by'
  | 'approved_at'
>;
export type AnnouncementUpdateFormData = Partial<
  Omit<
    Announcement,
    | 'id'
    | 'created_at'
    | 'updated_at'
    | 'total_recipients'
    | 'delivery_count'
    | 'read_count'
    | 'engagement_count'
    | 'contains_pii'
    | 'ferpa_approved'
    | 'approved_by'
    | 'approved_at'
  >
>;

export interface Evaluation {
  id: string;
  teacherId: string;
  teacher?: User;
  evaluatorId: string;
  evaluator?: User;
  schoolId: string;
  school?: School;
  academicYear: string;
  evaluationType: 'formal' | 'informal' | 'summative';
  status: 'draft' | 'in_progress' | 'completed' | 'submitted';
  overallRating?: EvaluationRating;
  components: EvaluationComponent[];
  comments?: string;
  recommendations?: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  dueDate?: string;
}

export interface EvaluationComponent {
  id: string;
  name: string;
  description: string;
  rating?: EvaluationRating;
  evidence?: string;
  comments?: string;
  weight: number;
  subComponents?: EvaluationSubComponent[];
}

export interface EvaluationSubComponent {
  id: string;
  name: string;
  description: string;
  rating?: EvaluationRating;
  evidence?: string;
  comments?: string;
}

// Enums
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  STATE_VIEWER = 'state_viewer',
  DISTRICT_ADMIN = 'district_admin',
  SCHOOL_ADMIN = 'school_admin',
  PRINCIPAL = 'principal',
  ASSISTANT_PRINCIPAL = 'assistant_principal',
  EVALUATOR = 'evaluator',
  TEACHER = 'teacher',
  PARENT = 'parent',
}

export enum GradeLevel {
  ELEMENTARY = 'elementary',
  MIDDLE = 'middle',
  HIGH = 'high',
  K12 = 'k12',
}

export enum SchoolType {
  PUBLIC = 'public',
  PRIVATE = 'private',
  CHARTER = 'charter',
  MAGNET = 'magnet',
}

export enum EvaluationRating {
  EXCELLENT = 'excellent',
  PROFICIENT = 'proficient',
  DEVELOPING = 'developing',
  UNSATISFACTORY = 'unsatisfactory',
  NOT_EVALUATED = 'not_evaluated',
}

// API Response Types
export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
}

export interface PaginatedResponse<T = unknown> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// Authentication Types
export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterData {
  username: string;
  email: string;
  full_name: string;
  password: string;
  role: string;
  district_id?: number;
  school_id?: number;
}

// UI Component Types
export interface ComponentProps {
  className?: string;
  children?: React.ReactNode;
  id?: string;
  'data-testid'?: string;
}

export interface TableColumn<T = Record<string, unknown>> {
  key: keyof T | string;
  title: string;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  render?: (value: unknown, record: T, index: number) => React.ReactNode;
}

export interface FormField {
  name: string;
  label: string;
  type:
    | 'text'
    | 'email'
    | 'password'
    | 'number'
    | 'select'
    | 'textarea'
    | 'checkbox'
    | 'radio'
    | 'date';
  required?: boolean;
  placeholder?: string;
  options?: { label: string; value: string | number }[];
  validation?: {
    pattern?: RegExp;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    custom?: (value: unknown) => string | undefined;
  };
}

export interface SelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
  group?: string;
}

export interface MenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  permission?: string;
  badge?: string | number;
}

// Error Types
export interface ApiError {
  message: string;
  code?: string;
  field?: string;
  details?: Record<string, unknown>;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Filter and Search Types
export interface FilterOptions {
  search?: string;
  role?: UserRole;
  status?: 'active' | 'inactive';
  districtId?: string;
  schoolId?: string;
  gradeLevel?: GradeLevel;
  schoolType?: SchoolType;
  dateFrom?: string;
  dateTo?: string;
}

export interface SortOptions {
  field: string;
  direction: 'asc' | 'desc';
}

// Form and Validation Types
export interface FormState<T = Record<string, unknown>> {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  touched: Partial<Record<keyof T, boolean>>;
  isSubmitting: boolean;
  isValid: boolean;
}

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: unknown) => string | undefined;
}

// Notification Types
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Theme and UI Types
export interface Theme {
  mode: 'light' | 'dark';
  primaryColor: string;
  fontSize: 'small' | 'medium' | 'large';
  compactMode: boolean;
}

export interface UserPreferences {
  theme: Theme;
  language: string;
  timezone: string;
  notifications: {
    email: boolean;
    push: boolean;
    inApp: boolean;
  };
  dashboard: {
    layout: 'grid' | 'list';
    widgets: string[];
  };
}

// Export all types for easy importing
export type {
  // Re-export React types that are commonly used
  ComponentProps as ReactComponentProps,
} from 'react';
